import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, Tag, Space,
  message, Popconfirm, Tabs, Typography, Alert, InputNumber,
  Tooltip, Badge, Spin, Radio, AutoComplete, Progress, Drawer, Empty,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, KeyOutlined, EyeInvisibleOutlined,
  CopyOutlined, CheckCircleOutlined, CloseCircleOutlined,
  LineChartOutlined, ReloadOutlined, DollarOutlined, SyncOutlined,
  EditOutlined, SearchOutlined, WarningOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import AppPageLayout from './common/AppPageLayout';
import PageHeader from './common/PageHeader';
import { EXECUTOR_API, BUILDER_API } from '../types';
import { useAdminGuard } from '../hooks/useAdminGuard';
import { useUser } from '../contexts/UserContext';
import { buildAuthHeaders } from '../utils/userStore';
import {
  assignTypeOf,
  buildVirtualKeyBody,
  changedVirtualKeyFields,
  formValuesOf,
  type BudgetPeriod,
  type VirtualKeyFormValues,
} from '../utils/virtualKeyForm';

// ── Auth helper: same pattern used by UsersManagementPanel ──
//   These endpoints are admin-gated by `require_admin` in the executor,
//   which reads the IAP `X-Goog-Authenticated-User-Email` header. We
//   replicate the IAP prefix locally so dev (no IAP) also works.
// La identidad y el tenant se toman del store (fuente única); el email se
// mantiene en la firma por compatibilidad con las llamadas existentes.
function authHeaders(_email: string | null, extra: Record<string, string> = {}): Record<string, string> {
  return buildAuthHeaders(extra);
}

const { Text, Paragraph } = Typography;

// ── Types ────────────────────────────────────────────────────────────────────

type ProviderName = 'gemini' | 'vertex_ai' | 'anthropic' | 'openai' | 'deepseek';

interface ProviderKey {
  id: string;
  provider: ProviderName;
  config?: { region?: string };
  label: string;
  // Curated catalogue. null = uncurated → callers fall back to live discovery.
  enabled_models: string[] | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
}

/** Response of POST /proxy/provider-keys/discover-models and its GET twin. */
interface DiscoveryResult {
  provider: string;
  models: string[];
  source: 'live' | 'static_fallback';
  reason: string | null;
  warning: string | null;
  enabled_models?: string[] | null;
  unpriced_models: string[];
  price_sync_error: boolean;
}

/** One entry of GET /proxy/provider-key-models. */
interface ProviderKeyModels {
  id: string;
  provider: ProviderName;
  label: string;
  models: string[];
  source: 'curated' | 'live' | 'static';
  enabled_models: string[] | null;
}


interface VirtualKey {
  id: string;
  key_prefix: string;
  label: string;
  provider_key_id: string;
  rate_limit_rpm: number | null;
  rate_limit_tpd: number | null;
  assigned_team_id: string | null;
  assigned_email: string | null;
  assigned_agent_id: string | null;
  budget_amount: number | null;
  budget_period: BudgetPeriod;
  budget_reset_at: string | null;
  spend: number;
  allowed_models: string[] | null;
  metadata: Record<string, unknown>;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
}

interface TeamOption { id: string; name: string }
interface UserOption { email: string; role: string }
interface AgentOption { id: string; name: string }

interface UsageRow {
  id: string;
  ts: string;
  provider: string;
  model: string;
  agent_id: string | null;
  input_tokens: number;
  output_tokens: number;
  cache_write_tokens: number;
  cache_read_tokens: number;
  cost_usd: number;
}

// Display helper: spend can be sub-cent, so widen the decimal precision
// progressively for small values rather than squashing them to "$0.00".
function formatUsd(value: number): string {
  const digits = !value ? 2 : value < 0.01 ? 4 : value < 1 ? 3 : 2;
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: digits, maximumFractionDigits: digits,
  }).format(value);
}

const BUDGET_PERIOD_LABEL: Record<BudgetPeriod, string> = {
  unlimited: 'Sin límite',
  daily: 'Diario',
  weekly: 'Semanal',
  monthly: 'Mensual',
};

const LogsSummary: React.FC<{ logs: UsageRow[] }> = ({ logs }) => {
  const total = useMemo(
    () => logs.reduce((sum, r) => sum + r.cost_usd, 0),
    [logs],
  );
  return (
    <>
      <div style={{ marginBottom: 12, color: '#8c8c8c', fontSize: 12 }}>
        {logs.length} llamada{logs.length === 1 ? '' : 's'} · total {formatUsd(total)}
      </div>
    </>
  );
};

// ── Provider colours ─────────────────────────────────────────────────────────

const PROVIDER_COLOR: Record<string, string> = {
  gemini: 'blue',
  vertex_ai: 'geekblue',
  anthropic: 'purple',
  openai: 'green',
  deepseek: 'magenta',
};

const PROVIDER_LABELS: Record<string, string> = {
  gemini: 'Google Gemini (AI Studio)',
  vertex_ai: 'Google Vertex AI',
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  deepseek: 'DeepSeek',
};

const VERTEX_REGIONS = [
  'europe-west1',
  'europe-west4',
  'us-central1',
  'us-east1',
  'asia-northeast1',
  'global',
];

// ── Provider Keys tab ────────────────────────────────────────────────────────

const ProviderKeysTab: React.FC = () => {
  const { email } = useUser();
  const [keys, setKeys] = useState<ProviderKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  // null = the modal is in "create" mode; otherwise we're editing that key.
  const [editing, setEditing] = useState<ProviderKey | null>(null);
  const [discovering, setDiscovering] = useState(false);
  const [discovered, setDiscovered] = useState<string[]>([]);
  const [discoveryNote, setDiscoveryNote] = useState<string | null>(null);
  const [unpricedModels, setUnpricedModels] = useState<string[]>([]);
  const [priceSyncError, setPriceSyncError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${EXECUTOR_API}/proxy/provider-keys`, { headers: authHeaders(email) });
      if (!res.ok) throw new Error('Failed to load provider keys');
      setKeys(await res.json());
    } catch {
      message.error('Error cargando provider keys');
    } finally {
      setLoading(false);
    }
  }, [email]);

  useEffect(() => { void load(); }, [load]);

  const resetModal = () => {
    setModalOpen(false);
    setEditing(null);
    setDiscovered([]);
    setDiscoveryNote(null);
    setUnpricedModels([]);
    setPriceSyncError(false);
    form.resetFields();
  };

  const openCreate = () => {
    setEditing(null);
    setDiscovered([]);
    setDiscoveryNote(null);
    setUnpricedModels([]);
    setPriceSyncError(false);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (row: ProviderKey) => {
    setEditing(row);
    setDiscovered(row.enabled_models ?? []);
    setDiscoveryNote(null);
    setUnpricedModels([]);
    setPriceSyncError(false);
    form.setFieldsValue({
      provider: row.provider,
      label: row.label,
      api_key: '',
      region: row.config?.region,
      enabled_models: row.enabled_models ?? [],
    });
    setModalOpen(true);
  };

  const applyDiscovery = (data: DiscoveryResult) => {
    setDiscovered(data.models);
    setDiscoveryNote(data.warning);
    setUnpricedModels(data.unpriced_models);
    setPriceSyncError(data.price_sync_error);
    if (data.warning) message.warning(data.warning);
    else message.success(`${data.models.length} modelos encontrados`);
  };

  /** Fetch the live catalogue so the admin curates from real models.
   *  A failure here never blocks saving — the picker stays in tags mode. */
  const handleDiscover = async () => {
    const provider = form.getFieldValue('provider') as string | undefined;
    const apiKey = form.getFieldValue('api_key') as string | undefined;
    const region = form.getFieldValue('region') as string | undefined;
    if (!provider) {
      message.warning('Selecciona primero un proveedor');
      return;
    }
    setDiscovering(true);
    setDiscoveryNote(null);
    try {
      // Editing without retyping the secret → discover with the stored one.
      const useStored = Boolean(editing) && !apiKey;
      const res = useStored
        ? await fetch(
            `${EXECUTOR_API}/proxy/provider-keys/${editing!.id}/discover-models?refresh=true`,
            { headers: authHeaders(email) },
          )
        : await fetch(`${EXECUTOR_API}/proxy/provider-keys/discover-models`, {
            method: 'POST',
            headers: authHeaders(email, { 'Content-Type': 'application/json' }),
            body: JSON.stringify({
              provider,
              api_key: apiKey ?? '',
              config: provider === 'vertex_ai' && region ? { region } : undefined,
            }),
          });
      if (!res.ok) {
        const err = await res.json().catch(() => ({} as { detail?: string }));
        throw new Error(err.detail ?? 'No se pudieron descubrir los modelos');
      }
      applyDiscovery(await res.json() as DiscoveryResult);
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Error descubriendo modelos');
    } finally {
      setDiscovering(false);
    }
  };

  const handleSubmit = async (values: {
    provider: string;
    label: string;
    api_key?: string;
    region?: string;
    enabled_models?: string[];
  }) => {
    setSaving(true);
    try {
      const config = values.provider === 'vertex_ai' && values.region
        ? { region: values.region }
        : undefined;

      const res = editing
        ? await fetch(`${EXECUTOR_API}/proxy/provider-keys/${editing.id}`, {
            method: 'PATCH',
            headers: authHeaders(email, { 'Content-Type': 'application/json' }),
            // api_key omitted when blank → the backend keeps the stored secret.
            body: JSON.stringify({
              label: values.label,
              ...(values.api_key ? { api_key: values.api_key } : {}),
              ...(config ? { config } : {}),
              enabled_models: values.enabled_models ?? [],
            }),
          })
        : await fetch(`${EXECUTOR_API}/proxy/provider-keys`, {
            method: 'POST',
            headers: authHeaders(email, { 'Content-Type': 'application/json' }),
            body: JSON.stringify({
              provider: values.provider,
              label: values.label,
              api_key: values.api_key,
              ...(config ? { config } : {}),
              enabled_models: values.enabled_models ?? null,
            }),
          });

      if (!res.ok) {
        const err = await res.json().catch(() => ({} as { detail?: string }));
        throw new Error(err.detail ?? 'Failed to save provider key');
      }
      const saved = await res.json().catch(() => ({} as { unpriced_models?: string[] }));
      message.success(editing ? 'Provider key actualizada' : 'Provider key añadida');
      if (saved.unpriced_models && saved.unpriced_models.length > 0) {
        message.warning(`${saved.unpriced_models.length} modelo(s) sin precio registrado`);
      }
      resetModal();
      void load();
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Error guardando provider key');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const res = await fetch(`${EXECUTOR_API}/proxy/provider-keys/${id}`, {
        method: 'DELETE',
        headers: authHeaders(email),
      });
      if (!res.ok) throw new Error('Failed to delete');
      message.success('Provider key eliminada');
      void load();
    } catch {
      message.error('Error eliminando provider key');
    }
  };

  const columns: ColumnsType<ProviderKey> = [
    {
      title: 'Proveedor',
      dataIndex: 'provider',
      key: 'provider',
      width: 180,
      render: (p: string) => (
        <Tag color={PROVIDER_COLOR[p] ?? 'default'} style={{ margin: 0 }}>
          {PROVIDER_LABELS[p] ?? p}
        </Tag>
      ),
    },
    {
      title: 'Etiqueta',
      dataIndex: 'label',
      key: 'label',
      ellipsis: { showTitle: true },
    },
    {
      title: 'Clave',
      key: 'key',
      width: 160,
      render: () => (
        <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 12 }}>
          <EyeInvisibleOutlined /> ••••••••••••••••
        </Text>
      ),
    },
    {
      title: 'Modelos',
      key: 'enabled_models',
      width: 190,
      render: (_: unknown, row: ProviderKey) => {
        if (!row.enabled_models || row.enabled_models.length === 0) {
          return (
            <Tooltip title="Sin curar — se ofrecerá el catálogo completo del proveedor">
              <Text type="secondary">Todos</Text>
            </Tooltip>
          );
        }
        const visible = row.enabled_models.slice(0, 2);
        const rest = row.enabled_models.length - visible.length;
        return (
          <Tooltip title={row.enabled_models.join(', ')}>
            <Space size={2} wrap>
              {visible.map(m => <Tag key={m} style={{ fontSize: 10, margin: 0 }}>{m}</Tag>)}
              {rest > 0 && <Tag style={{ fontSize: 10, margin: 0 }}>+{rest}</Tag>}
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: 'Estado',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (active: boolean) =>
        active
          ? <Badge status="success" text="Activa" />
          : <Badge status="error" text="Inactiva" />,
    },
    {
      title: 'Creada por',
      dataIndex: 'created_by',
      key: 'created_by',
      width: 200,
      render: (v: string | null) => v
        ? <Tooltip title={v}><Text style={{ fontSize: 12 }} ellipsis>{v}</Text></Tooltip>
        : '—',
    },
    {
      title: 'Fecha',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 110,
      render: (v: string) => new Date(v).toLocaleDateString(),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      fixed: 'right',
      render: (_: unknown, row: ProviderKey) => (
        <Space size={4}>
          <Tooltip title="Editar (etiqueta, modelos, rotar clave)">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          </Tooltip>
          <Popconfirm
            title="¿Eliminar esta provider key?"
            description="Se eliminarán también todas las claves virtuales asociadas."
            onConfirm={() => void handleDelete(row.id)}
            okText="Eliminar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
          >
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Añadir provider key
        </Button>
      </div>

      <Table
        dataSource={keys}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{ pageSize: 20, showSizeChanger: false, hideOnSinglePage: true }}
        scroll={{ x: 'max-content' }}
      />

      <Modal
        title={editing ? `Editar ${editing.label}` : 'Añadir API key de proveedor'}
        open={modalOpen}
        onCancel={resetModal}
        onOk={() => form.submit()}
        confirmLoading={saving}
        okText={editing ? 'Guardar' : 'Añadir'}
        width={620}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: 16 }}>
          <Form.Item
            name="provider"
            label="Proveedor"
            rules={[{ required: true }]}
            extra={editing ? 'El proveedor no se puede cambiar — crea otra clave' : undefined}
          >
            {/* Locked when editing: the stored credential belongs to this provider. */}
            <Select placeholder="Selecciona proveedor" disabled={Boolean(editing)}>
              <Select.Option value="gemini">Google Gemini (AI Studio)</Select.Option>
              <Select.Option value="vertex_ai">Google Vertex AI (Service Account)</Select.Option>
              <Select.Option value="anthropic">Anthropic</Select.Option>
              <Select.Option value="openai">OpenAI</Select.Option>
              <Select.Option value="deepseek">DeepSeek</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="label" label="Etiqueta" rules={[{ required: true }]}>
            <Input placeholder="Ej: Vertex AI - Cliente X" />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.provider !== curr.provider}
          >
            {({ getFieldValue }) => {
              const provider = getFieldValue('provider');
              // When editing, a blank secret means "keep the stored one".
              const keyRequired = !editing;
              const keepHint = editing ? 'Déjalo vacío para conservar la clave actual' : undefined;
              if (provider === 'vertex_ai') {
                return (
                  <>
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message="Credencial sensible"
                      description="El JSON de Service Account da acceso a TODO el proyecto GCP del usuario. Trátalo como una contraseña: cifrado en reposo, no copiarlo a logs."
                    />
                    <Form.Item
                      name="api_key"
                      label="Service Account JSON"
                      extra={keepHint}
                      rules={keyRequired ? [{ required: true, message: 'Pega el JSON completo de la SA' }] : []}
                    >
                      <Input.TextArea
                        rows={8}
                        placeholder='{"type":"service_account","project_id":"my-project","private_key":"-----BEGIN PRIVATE KEY-----\n..."}'
                        style={{ fontFamily: 'monospace', fontSize: 11 }}
                      />
                    </Form.Item>
                    <Form.Item
                      name="region"
                      label="Region"
                      rules={[{ required: true }]}
                      initialValue="europe-west1"
                    >
                      <Select options={VERTEX_REGIONS.map((r) => ({ value: r, label: r }))} />
                    </Form.Item>
                  </>
                );
              }
              return (
                <Form.Item
                  name="api_key"
                  label="API Key"
                  extra={keepHint}
                  rules={keyRequired ? [{ required: true }] : []}
                >
                  <Input.Password placeholder="AIza... / sk-ant-... / sk-..." />
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item
            label={
              <Space>
                Modelos
                <Button
                  size="small"
                  icon={<SearchOutlined />}
                  loading={discovering}
                  onClick={() => void handleDiscover()}
                >
                  Descubrir modelos
                </Button>
              </Space>
            }
            name="enabled_models"
            extra={
              discovered.length > 0
                ? `${discovered.length} modelos descubiertos. Los seleccionados serán los únicos ofrecidos al crear claves virtuales de este proveedor. Vacío = catálogo completo.`
                : 'Opcional. Pulsa "Descubrir modelos" para traer el catálogo real del proveedor. Vacío = se ofrecerá el catálogo completo.'
            }
          >
            <Select
              mode="tags"
              placeholder="Sin curar — se ofrecerán todos los modelos del proveedor"
              style={{ width: '100%' }}
              tokenSeparators={[',']}
              showSearch
              allowClear
              options={discovered.map((m) => ({
                value: m,
                label: unpricedModels.includes(m)
                  ? (
                    <Space size={4}>
                      {m}
                      <Tooltip title="Sin precio registrado — facturará $0 hasta que se sincronice">
                        <WarningOutlined style={{ color: '#faad14' }} />
                      </Tooltip>
                    </Space>
                  )
                  : m,
              }))}
            />
          </Form.Item>

          {discoveryNote && (
            <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={discoveryNote} />
          )}
          {unpricedModels.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Modelos sin precio"
              description={`Estos modelos facturarán $0 hasta sincronizar precios: ${unpricedModels.join(', ')}`}
            />
          )}
          {priceSyncError && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="No pudimos verificar precios ahora mismo"
              description="Probá de nuevo o usá el botón de sincronizar en la pestaña Precios."
            />
          )}
          {editing && (
            <Alert
              type="info"
              showIcon
              message="Los cambios no afectan a claves virtuales ya creadas: sus modelos permitidos se mantienen tal cual."
            />
          )}
        </Form>
      </Modal>
    </>
  );
};

// ── Virtual Keys tab ─────────────────────────────────────────────────────────

const VirtualKeysTab: React.FC = () => {
  const { email } = useUser();
  const [keys, setKeys] = useState<VirtualKey[]>([]);
  const [providerKeys, setProviderKeys] = useState<ProviderKey[]>([]);
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [agents, setAgents] = useState<AgentOption[]>([]);
  // Models keyed by provider key id — the allowlist is scoped to whichever
  // provider key the virtual key is bound to, not a union across providers.
  const [modelsByProviderKey, setModelsByProviderKey] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [form] = Form.useForm();
  const [assignType, setAssignType] = useState<'none' | 'team' | 'user' | 'agent'>('none');
  const [editingKey, setEditingKey] = useState<VirtualKey | null>(null);
  const [statusFilter, setStatusFilter] = useState<'active' | 'revoked' | 'all'>('active');
  const [logsKey, setLogsKey] = useState<VirtualKey | null>(null);
  const [logs, setLogs] = useState<UsageRow[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const openLogs = useCallback(async (key: VirtualKey) => {
    setLogsKey(key);
    setLogsLoading(true);
    try {
      const res = await fetch(
        `${EXECUTOR_API}/proxy/virtual-keys/${key.id}/usage?limit=100`,
        { headers: authHeaders(email) },
      );
      setLogs(res.ok ? (await res.json() as UsageRow[]) : []);
    } catch {
      message.error('Error cargando logs');
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, [email]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [vkRes, pkRes, teamsRes, usersRes, agentsRes, modelsRes] = await Promise.all([
        fetch(`${EXECUTOR_API}/proxy/virtual-keys`,         { headers: authHeaders(email) }),
        fetch(`${EXECUTOR_API}/proxy/provider-keys`,        { headers: authHeaders(email) }),
        fetch(`${EXECUTOR_API}/agent/builder/teams`,        { headers: authHeaders(email) }),
        fetch(`${EXECUTOR_API}/agent/builder/users`,        { headers: authHeaders(email) }),
        // The agents endpoint lives on builder-backend, not executor.
        fetch(`${BUILDER_API}/agent/builder/agents`,        { headers: authHeaders(email) }),
        fetch(`${EXECUTOR_API}/proxy/provider-key-models`,  { headers: authHeaders(email) }),
      ]);
      if (!vkRes.ok || !pkRes.ok) throw new Error('Failed to load keys');
      const [vk, pk]: [VirtualKey[], ProviderKey[]] = await Promise.all([vkRes.json(), pkRes.json()]);
      setKeys(vk);
      setProviderKeys(pk);
      // Both endpoints wrap their list in a top-level key — unwrap before set.
      if (teamsRes.ok) {
        const tj = await teamsRes.json() as { teams?: TeamOption[] };
        setTeams(Array.isArray(tj?.teams) ? tj.teams : []);
      }
      if (usersRes.ok) {
        const uj = await usersRes.json() as { users?: UserOption[] };
        setUsers(Array.isArray(uj?.users) ? uj.users : []);
      }
      if (agentsRes.ok) {
        const aj = await agentsRes.json() as { agents?: AgentOption[] };
        setAgents(Array.isArray(aj?.agents) ? aj.agents : []);
      }
      if (modelsRes.ok) {
        const mj = await modelsRes.json() as { provider_keys?: ProviderKeyModels[] };
        // Keep the per-key grouping: an allowlist entry only means anything
        // against the provider key the virtual key actually routes through.
        setModelsByProviderKey(
          Object.fromEntries((mj.provider_keys ?? []).map(pk => [pk.id, pk.models])),
        );
      }
    } catch {
      message.error('Error cargando virtual keys');
    } finally {
      setLoading(false);
    }
  }, [email]);

  useEffect(() => { void load(); }, [load]);

  // Re-fetch when the tab regains focus so spend updates after a proxy
  // call made from another window/curl are visible without a hard reload.
  // Skip when the page itself isn't visible (alt-tab to another app while
  // this tab was already focused) to avoid a wasted fetch.
  useEffect(() => {
    const onFocus = (): void => {
      if (document.visibilityState === 'visible') void load();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const errorDetail = async (res: Response, fallback: string): Promise<string> => {
    const data = (await res.json().catch(() => ({}))) as { detail?: unknown };
    return typeof data.detail === 'string' ? data.detail : fallback;
  };

  const handleCreate = async (values: VirtualKeyFormValues) => {
    setSaving(true);
    try {
      const body = { ...buildVirtualKeyBody(values, assignType), provider_key_id: values.provider_key_id };
      const res = await fetch(`${EXECUTOR_API}/proxy/virtual-keys`, {
        method: 'POST',
        headers: authHeaders(email, { 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await errorDetail(res, 'Error creando virtual key'));
      const data: VirtualKey & { key: string } = await res.json();
      setNewKey(data.key);
      form.resetFields();
      void load();
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Error creando virtual key');
    } finally {
      setSaving(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false); setNewKey(null); setEditingKey(null);
    form.resetFields(); setAssignType('none');
  };

  const openCreate = () => {
    closeModal();
    setModalOpen(true);
  };

  const openEdit = (row: VirtualKey) => {
    setEditingKey(row);
    setNewKey(null);
    setAssignType(assignTypeOf(row));
    form.setFieldsValue(formValuesOf(row));
    setModalOpen(true);
  };

  const handleUpdate = async (values: VirtualKeyFormValues) => {
    if (!editingKey) return;
    // Only what really changed: re-sending an unchanged budget period would
    // be harmless now, but the key's budget state must never depend on that.
    const changes = changedVirtualKeyFields(editingKey, buildVirtualKeyBody(values, assignType));
    if (Object.keys(changes).length === 0) {
      message.info('No hay cambios que guardar');
      closeModal();
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${EXECUTOR_API}/proxy/virtual-keys/${editingKey.id}`, {
        method: 'PATCH',
        headers: authHeaders(email, { 'Content-Type': 'application/json' }),
        body: JSON.stringify(changes),
      });
      if (!res.ok) throw new Error(await errorDetail(res, 'Error actualizando la clave'));
      message.success('Clave actualizada');
      closeModal();
      void load();
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Error actualizando la clave');
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (id: string) => {
    try {
      const res = await fetch(`${EXECUTOR_API}/proxy/virtual-keys/${id}`, {
        method: 'DELETE',
        headers: authHeaders(email),
      });
      if (!res.ok) throw new Error('Failed to revoke');
      message.success('Clave revocada');
      void load();
    } catch {
      message.error('Error revocando clave');
    }
  };

  const copyToClipboard = (text: string) => {
    void navigator.clipboard.writeText(text).then(() => message.success('Copiado'));
  };

  const teamName  = (id: string | null) => teams.find(t => t.id === id)?.name   ?? id ?? '—';
  const agentName = (id: string | null) => agents.find(a => a.id === id)?.name  ?? id ?? '—';

  const columns: ColumnsType<VirtualKey> = [
    {
      title: 'Etiqueta',
      dataIndex: 'label',
      key: 'label',
      width: 160,
      fixed: 'left',
      render: (label: string, row: VirtualKey) => (
        <Space direction="vertical" size={0} style={{ lineHeight: 1.2 }}>
          <Tooltip title="Editar clave">
            <Button
              type="link"
              onClick={() => openEdit(row)}
              style={{ fontWeight: 600, fontSize: 13, padding: 0, height: 'auto', maxWidth: 140, justifyContent: 'flex-start' }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
            </Button>
          </Tooltip>
          <Text code copyable={{ text: `${row.key_prefix}…` }} style={{ fontSize: 10, fontFamily: 'monospace', padding: 0 }}>
            {row.key_prefix}…
          </Text>
        </Space>
      ),
    },
    {
      title: 'Provider',
      dataIndex: 'provider_key_id',
      key: 'provider_key_id',
      width: 130,
      render: (id: string) => {
        const pk = providerKeys.find(p => p.id === id);
        if (!pk) return <Text type="secondary">—</Text>;
        return (
          <Tooltip title={`${PROVIDER_LABELS[pk.provider] ?? pk.provider} · ${pk.label}`}>
            <Tag color={PROVIDER_COLOR[pk.provider] ?? 'default'} style={{ margin: 0, maxWidth: '100%' }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', maxWidth: 110, verticalAlign: 'middle' }}>
                {PROVIDER_LABELS[pk.provider] ?? pk.provider}
              </span>
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Asignado a',
      key: 'assigned',
      width: 200,
      render: (_: unknown, row: VirtualKey) => {
        if (row.assigned_team_id) {
          const name = teamName(row.assigned_team_id);
          return <Tooltip title={`Equipo · ${name}`}><Tag color="blue" style={{ margin: 0, maxWidth: '100%' }}><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', maxWidth: 170, verticalAlign: 'middle' }}>👥 {name}</span></Tag></Tooltip>;
        }
        if (row.assigned_email) {
          return <Tooltip title={`Usuario · ${row.assigned_email}`}><Tag color="cyan" style={{ margin: 0, maxWidth: '100%' }}><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', maxWidth: 170, verticalAlign: 'middle' }}>👤 {row.assigned_email}</span></Tag></Tooltip>;
        }
        if (row.assigned_agent_id) {
          const name = agentName(row.assigned_agent_id);
          return <Tooltip title={`Agente · ${name}`}><Tag color="geekblue" style={{ margin: 0, maxWidth: '100%' }}><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', maxWidth: 170, verticalAlign: 'middle' }}>🤖 {name}</span></Tag></Tooltip>;
        }
        return <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Presupuesto · Gasto',
      key: 'budget',
      width: 220,
      render: (_: unknown, row: VirtualKey) => {
        const spend = row.spend ?? 0;
        if (row.budget_period === 'unlimited' || row.budget_amount == null) {
          return (
            <Space direction="vertical" size={0} style={{ lineHeight: 1.3 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Sin límite</Text>
              <Text style={{ fontSize: 11 }}>Gasto: {formatUsd(spend)}</Text>
            </Space>
          );
        }
        const pct = Math.min(100, (spend / row.budget_amount) * 100);
        const status = pct >= 100 ? 'exception' : pct >= 80 ? 'active' : 'normal';
        return (
          <Space direction="vertical" size={0} style={{ width: '100%', lineHeight: 1.3 }}>
            <Text style={{ fontSize: 12 }}>
              {formatUsd(spend)} / {formatUsd(row.budget_amount)} · {BUDGET_PERIOD_LABEL[row.budget_period]}
            </Text>
            <Progress
              percent={Math.max(0.5, pct)}
              showInfo={pct >= 1}
              format={(value) => `${Math.round(value ?? 0)}%`}
              size="small"
              status={status}
              style={{ margin: 0 }}
            />
            {row.budget_reset_at && (
              <Tooltip title={new Date(row.budget_reset_at).toLocaleString()}>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  Reset {new Date(row.budget_reset_at).toLocaleDateString()}
                </Text>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Modelos',
      key: 'allowed_models',
      width: 180,
      render: (_: unknown, row: VirtualKey) => {
        if (!row.allowed_models || row.allowed_models.length === 0) {
          return <Text type="secondary">Todos</Text>;
        }
        const visible = row.allowed_models.slice(0, 2);
        const rest = row.allowed_models.length - visible.length;
        return (
          <Tooltip title={row.allowed_models.join(', ')}>
            <Space size={2} wrap>
              {visible.map(m => <Tag key={m} style={{ fontSize: 10, margin: 0 }}>{m}</Tag>)}
              {rest > 0 && <Tag style={{ fontSize: 10, margin: 0 }}>+{rest}</Tag>}
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: 'Límites',
      key: 'limits',
      width: 110,
      render: (_: unknown, row: VirtualKey) => {
        const parts: string[] = [];
        if (row.rate_limit_rpm) parts.push(`${row.rate_limit_rpm} RPM`);
        if (row.rate_limit_tpd) parts.push(`${row.rate_limit_tpd} TPD`);
        return parts.length
          ? <Text type="secondary" style={{ fontSize: 11 }}>{parts.join(' · ')}</Text>
          : <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Creado por',
      dataIndex: 'created_by',
      key: 'created_by',
      width: 160,
      render: (v: string | null) => v
        ? <Tooltip title={v}><Text type="secondary" style={{ fontSize: 11 }} ellipsis>{v}</Text></Tooltip>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Estado',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 90,
      align: 'center',
      render: (active: boolean) =>
        active
          ? <Tag color="success" style={{ margin: 0 }}><CheckCircleOutlined /> Activa</Tag>
          : <Tag color="error" style={{ margin: 0 }}><CloseCircleOutlined /> Revocada</Tag>,
    },
    {
      title: 'Creada',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 100,
      render: (v: string) => <Text style={{ fontSize: 11 }}>{new Date(v).toLocaleDateString()}</Text>,
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_: unknown, row: VirtualKey) => (
        <Space size={4}>
          <Tooltip title="Ver logs de uso">
            <Button size="small" icon={<LineChartOutlined />} onClick={() => void openLogs(row)} />
          </Tooltip>
          {row.is_active && (
            <Popconfirm
              title="¿Revocar esta clave virtual?"
              description="Los agentes que la usen dejarán de funcionar."
              onConfirm={() => void handleRevoke(row.id)}
              okText="Revocar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Revocar">
                <Button danger size="small" icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const visibleKeys = keys.filter((k) =>
    statusFilter === 'all' || (statusFilter === 'active' ? k.is_active : !k.is_active));

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
        <Radio.Group value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <Radio.Button value="active">Activas ({keys.filter((k) => k.is_active).length})</Radio.Button>
          <Radio.Button value="revoked">Revocadas ({keys.filter((k) => !k.is_active).length})</Radio.Button>
          <Radio.Button value="all">Todas ({keys.length})</Radio.Button>
        </Radio.Group>
        <Space>
          <Tooltip title="Recargar para ver el gasto actualizado">
            <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading} />
          </Tooltip>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreate}
            disabled={providerKeys.length === 0}
          >
            Generar clave virtual
          </Button>
        </Space>
      </div>

      {providerKeys.length === 0 && (
        <Alert
          style={{ marginBottom: 12 }}
          type="warning"
          message="Añade primero una provider key en la pestaña Proveedores para poder generar claves virtuales."
        />
      )}

      <Table
        dataSource={visibleKeys}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{ pageSize: 20, showSizeChanger: false, hideOnSinglePage: true }}
        scroll={{ x: 'max-content' }}
      />

      <Drawer
        title={logsKey ? `Logs — ${logsKey.label} (${logsKey.key_prefix}…)` : 'Logs'}
        open={!!logsKey}
        width={760}
        onClose={() => { setLogsKey(null); setLogs([]); }}
      >
        {logsLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : logs.length === 0 ? (
          <Empty description="Sin llamadas registradas todavía" />
        ) : (
          <>
            <LogsSummary logs={logs} />
            <Table
              dataSource={logs}
              rowKey="id"
              size="small"
              pagination={{ pageSize: 20, showSizeChanger: false }}
              columns={[
                {
                  title: 'Fecha',
                  dataIndex: 'ts',
                  width: 140,
                  render: (ts: string) => <Text style={{ fontSize: 11 }}>{new Date(ts).toLocaleString()}</Text>,
                },
                {
                  title: 'Modelo',
                  key: 'model',
                  render: (_: unknown, r: UsageRow) => (
                    <Space size={4} direction="vertical">
                      <Text style={{ fontSize: 12, fontFamily: 'monospace' }}>{r.model}</Text>
                      <Tag color={PROVIDER_COLOR[r.provider] ?? 'default'} style={{ margin: 0, fontSize: 10 }}>
                        {r.provider}
                      </Tag>
                    </Space>
                  ),
                },
                {
                  title: 'Tokens (in / out)',
                  key: 'tokens',
                  align: 'right',
                  render: (_: unknown, r: UsageRow) => (
                    <Space size={0} direction="vertical" style={{ lineHeight: 1.3 }}>
                      <Text style={{ fontSize: 12, fontFamily: 'monospace' }}>
                        {r.input_tokens} / {r.output_tokens}
                      </Text>
                      {(r.cache_write_tokens > 0 || r.cache_read_tokens > 0) && (
                        <Tooltip title={`cache write: ${r.cache_write_tokens} · cache read: ${r.cache_read_tokens}`}>
                          <Text type="secondary" style={{ fontSize: 10, fontFamily: 'monospace' }}>
                            cache {r.cache_write_tokens} / {r.cache_read_tokens}
                          </Text>
                        </Tooltip>
                      )}
                    </Space>
                  ),
                },
                {
                  title: 'Coste',
                  dataIndex: 'cost_usd',
                  align: 'right',
                  width: 100,
                  render: (c: number) => <Text style={{ fontSize: 12 }}>{formatUsd(c)}</Text>,
                },
                {
                  title: 'Agente',
                  dataIndex: 'agent_id',
                  width: 160,
                  render: (id: string | null) =>
                    id ? <Text style={{ fontSize: 11 }}>{agentName(id)}</Text> : <Text type="secondary">—</Text>,
                },
              ]}
            />
          </>
        )}
      </Drawer>

      <Modal
        title={editingKey ? `Editar clave — ${editingKey.key_prefix}…` : 'Generar clave virtual'}
        open={modalOpen}
        onCancel={closeModal}
        footer={newKey ? (
          <Button type="primary" onClick={closeModal}>
            Cerrar
          </Button>
        ) : undefined}
        onOk={() => form.submit()}
        confirmLoading={saving}
        okText={editingKey ? 'Guardar' : 'Generar'}
        cancelText="Cancelar"
        destroyOnHidden
      >
        {newKey ? (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <CheckCircleOutlined style={{ fontSize: 32, color: '#52c41a', marginBottom: 12 }} />
            <Paragraph strong>Clave generada — cópiala ahora, no se mostrará de nuevo.</Paragraph>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center' }}>
              <Text code style={{ fontSize: 13 }}>{newKey}</Text>
              <Button
                icon={<CopyOutlined />}
                size="small"
                onClick={() => copyToClipboard(newKey)}
              >
                Copiar
              </Button>
            </div>
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={editingKey ? handleUpdate : handleCreate}
            initialValues={{ budget_period: 'unlimited' }}
            style={{ marginTop: 16 }}
          >
            <Form.Item name="label" label="Etiqueta" rules={[{ required: true }]}>
              <Input placeholder="Ej: Agente facturación prod" />
            </Form.Item>
            <Form.Item name="provider_key_id" label="Provider key" rules={[{ required: true }]}>
              <Select
                disabled={!!editingKey}
                placeholder="Selecciona provider key"
                // Models are provider-specific and allowed_models IS enforced at
                // request time, so a leftover selection from another provider
                // would 402 every call. Clear it on switch.
                onChange={() => form.setFieldValue('allowed_models', [])}
              >
                {providerKeys.map(pk => (
                  <Select.Option key={pk.id} value={pk.id}>
                    {PROVIDER_LABELS[pk.provider] ?? pk.provider} — {pk.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Asignar a">
              <Radio.Group value={assignType} onChange={(e) => setAssignType(e.target.value)}>
                <Radio.Button value="none">Sin asignar</Radio.Button>
                <Radio.Button value="team">Equipo</Radio.Button>
                <Radio.Button value="user">Usuario</Radio.Button>
                <Radio.Button value="agent">Agente</Radio.Button>
              </Radio.Group>
            </Form.Item>
            {assignType === 'team' && (
              <Form.Item name="assigned_team_id" label="Equipo" rules={[{ required: true }]}>
                <Select
                  placeholder={teams.length === 0 ? 'No hay equipos creados todavía' : 'Selecciona equipo'}
                  showSearch
                  optionFilterProp="label"
                  notFoundContent="Sin equipos"
                  options={teams.map(t => ({ value: t.id, label: t.name }))}
                />
              </Form.Item>
            )}
            {assignType === 'user' && (
              <Form.Item name="assigned_email" label="Usuario (email)" rules={[{ required: true, type: 'email' }]}>
                <AutoComplete
                  placeholder="usuario@cubicup.com"
                  options={users.map(u => ({ value: u.email, label: `${u.email} (${u.role})` }))}
                  filterOption={(input, option) =>
                    (option?.value ?? '').toString().toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            )}
            {assignType === 'agent' && (
              <Form.Item name="assigned_agent_id" label="Agente" rules={[{ required: true }]}>
                <Select
                  placeholder={agents.length === 0 ? 'No hay agentes creados todavía' : 'Selecciona agente'}
                  showSearch
                  optionFilterProp="label"
                  notFoundContent="Sin agentes"
                  options={agents.map(a => ({ value: a.id, label: a.name }))}
                />
              </Form.Item>
            )}

            <Form.Item label="Presupuesto">
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item name="budget_period" noStyle>
                  <Select style={{ width: 160 }}>
                    <Select.Option value="unlimited">Sin límite</Select.Option>
                    <Select.Option value="daily">Diario</Select.Option>
                    <Select.Option value="weekly">Semanal</Select.Option>
                    <Select.Option value="monthly">Mensual</Select.Option>
                  </Select>
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(p, c) => p.budget_period !== c.budget_period}>
                  {({ getFieldValue }) => {
                    const period = getFieldValue('budget_period') as BudgetPeriod;
                    return (
                      <Form.Item
                        name="budget_amount"
                        noStyle
                        rules={period !== 'unlimited' ? [{ required: true, message: 'Indica el importe' }] : []}
                      >
                        <InputNumber
                          min={0}
                          step={1}
                          style={{ width: '100%' }}
                          disabled={period === 'unlimited'}
                          placeholder={period === 'unlimited' ? 'N/A' : 'Importe €'}
                          addonAfter="$"
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Space.Compact>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) => prev.provider_key_id !== curr.provider_key_id}
            >
              {({ getFieldValue }) => {
                const pkId = getFieldValue('provider_key_id') as string | undefined;
                // Scoped to the chosen provider key: offering a union across
                // providers is what let gpt-4o land on a Gemini-backed key.
                const models = pkId ? modelsByProviderKey[pkId] ?? [] : [];
                return (
                  <Form.Item
                    name="allowed_models"
                    label="Modelos permitidos"
                    extra={
                      !pkId
                        ? 'Selecciona antes una provider key para ver sus modelos.'
                        : models.length > 0
                          ? `Opcional. ${models.length} modelos disponibles en esta provider key. Vacío = cualquier modelo.`
                          : 'Opcional. Si lo dejas vacío, esta clave puede invocar cualquier modelo.'
                    }
                  >
                    <Select
                      mode="tags"
                      placeholder={pkId ? 'Vacío = cualquier modelo' : 'Selecciona una provider key primero'}
                      style={{ width: '100%' }}
                      tokenSeparators={[',']}
                      showSearch
                      allowClear
                      disabled={!pkId}
                      options={models.map((m) => ({ value: m, label: m }))}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>

            <Form.Item name="rate_limit_rpm" label="Límite peticiones/minuto (RPM)" extra="Opcional">
              <InputNumber min={1} max={10000} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="rate_limit_tpd" label="Límite tokens/día (TPD)" extra="Opcional">
              <InputNumber min={1000} max={100000000} style={{ width: '100%' }} />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </>
  );
};

// ── Model Prices tab ─────────────────────────────────────────────────────────

interface ModelPrice {
  provider: string;
  model: string;
  input_usd_per_1m_tok: number;
  output_usd_per_1m_tok: number;
  cache_write_usd_per_1m_tok: number;
  cache_read_usd_per_1m_tok: number;
  last_synced_at: string | null;
  updated_at: string;
}

const ModelPricesTab: React.FC = () => {
  const { email } = useUser();
  const [prices, setPrices] = useState<ModelPrice[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${EXECUTOR_API}/proxy/model-prices`, { headers: authHeaders(email) });
      if (!res.ok) throw new Error('Failed');
      setPrices(await res.json() as ModelPrice[]);
    } catch {
      message.error('Error cargando precios');
    } finally {
      setLoading(false);
    }
  }, [email]);

  useEffect(() => { void load(); }, [load]);

  const handleSync = async (): Promise<void> => {
    setSyncing(true);
    try {
      const res = await fetch(`${EXECUTOR_API}/proxy/model-prices/sync`, {
        method: 'POST',
        headers: authHeaders(email),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { detail?: string }).detail ?? 'Sync failed');
      }
      const data = await res.json() as { inserted: number; updated: number; skipped: number };
      message.success(`Sincronizado: ${data.inserted} nuevos, ${data.updated} actualizados, ${data.skipped} ignorados`);
      void load();
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Error sincronizando');
    } finally {
      setSyncing(false);
    }
  };

  const columns: ColumnsType<ModelPrice> = [
    {
      title: 'Proveedor',
      dataIndex: 'provider',
      key: 'provider',
      width: 130,
      filters: Array.from(new Set(prices.map(p => p.provider))).map(p => ({ text: PROVIDER_LABELS[p] ?? p, value: p })),
      onFilter: (value, row) => row.provider === value,
      render: (p: string) => <Tag color={PROVIDER_COLOR[p] ?? 'default'} style={{ margin: 0 }}>{PROVIDER_LABELS[p] ?? p}</Tag>,
    },
    {
      title: 'Modelo',
      dataIndex: 'model',
      key: 'model',
      sorter: (a, b) => a.model.localeCompare(b.model),
      render: (m: string) => <Text code style={{ fontSize: 12 }}>{m}</Text>,
    },
    {
      title: 'Input ($/1M tokens)',
      dataIndex: 'input_usd_per_1m_tok',
      key: 'input',
      width: 170,
      align: 'right',
      sorter: (a, b) => a.input_usd_per_1m_tok - b.input_usd_per_1m_tok,
      render: (v: number) => <Text style={{ fontSize: 12 }}>{formatUsd(v)}</Text>,
    },
    {
      title: 'Output ($/1M tokens)',
      dataIndex: 'output_usd_per_1m_tok',
      key: 'output',
      width: 170,
      align: 'right',
      sorter: (a, b) => a.output_usd_per_1m_tok - b.output_usd_per_1m_tok,
      render: (v: number) => <Text style={{ fontSize: 12 }}>{formatUsd(v)}</Text>,
    },
    {
      title: 'Cache write ($/1M tokens)',
      dataIndex: 'cache_write_usd_per_1m_tok',
      key: 'cache_write',
      width: 170,
      align: 'right',
      sorter: (a, b) => a.cache_write_usd_per_1m_tok - b.cache_write_usd_per_1m_tok,
      render: (v: number) => <Text style={{ fontSize: 12 }}>{formatUsd(v)}</Text>,
    },
    {
      title: 'Cache read ($/1M tokens)',
      dataIndex: 'cache_read_usd_per_1m_tok',
      key: 'cache_read',
      width: 170,
      align: 'right',
      sorter: (a, b) => a.cache_read_usd_per_1m_tok - b.cache_read_usd_per_1m_tok,
      render: (v: number) => <Text style={{ fontSize: 12 }}>{formatUsd(v)}</Text>,
    },
    {
      title: 'Última sincro',
      dataIndex: 'last_synced_at',
      key: 'last_synced_at',
      width: 140,
      render: (v: string | null) => v
        ? <Tooltip title={new Date(v).toLocaleString()}><Text style={{ fontSize: 11 }}>{new Date(v).toLocaleDateString()}</Text></Tooltip>
        : <Text type="secondary" style={{ fontSize: 11 }}>Manual</Text>,
    },
  ];

  return (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Precios en USD por 1M de tokens"
        description={
          <>
            Sincronizados desde <Text code>LiteLLM/model_prices_and_context_window.json</Text> —
            actualización automática los lunes 03:00 UTC. La sincronización solo afecta a los
            providers que usamos (gemini, vertex_ai, anthropic, openai). Los costes históricos
            ya registrados no se recalculan al cambiar precios.
          </>
        }
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12, gap: 8 }}>
        <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading}>
          Recargar
        </Button>
        <Button
          type="primary"
          icon={<SyncOutlined spin={syncing} />}
          onClick={() => void handleSync()}
          loading={syncing}
        >
          Sincronizar con LiteLLM
        </Button>
      </div>
      <Table
        dataSource={prices}
        columns={columns}
        rowKey={(r) => `${r.provider}/${r.model}`}
        loading={loading}
        size="small"
        pagination={{ pageSize: 30, showSizeChanger: false, hideOnSinglePage: true }}
        scroll={{ x: 'max-content' }}
      />
    </>
  );
};


// ── Main page ─────────────────────────────────────────────────────────────────

const ModelTokensPage: React.FC = () => {
  const { loading } = useAdminGuard();

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
  <AppPageLayout maxWidth={1600}>
    <PageHeader
      icon={<KeyOutlined />}
      title="Tokens LLM"
      subtitle="Gestiona las API keys de proveedores y las claves virtuales del proxy"
    />
    <Tabs
      defaultActiveKey="providers"
      style={{ marginTop: 8 }}
      items={[
        {
          key: 'providers',
          label: <Space><KeyOutlined />Proveedores</Space>,
          children: <ProviderKeysTab />,
        },
        {
          key: 'virtual',
          label: <Space><EyeInvisibleOutlined />Claves virtuales</Space>,
          children: <VirtualKeysTab />,
        },
        {
          key: 'prices',
          label: <Space><DollarOutlined />Precios</Space>,
          children: <ModelPricesTab />,
        },
        {
          key: 'proxy',
          label: 'Uso del proxy',
          children: (
            <div style={{ maxWidth: 680 }}>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Dos protocolos"
                description={
                  <>
                    El proxy habla <strong>OpenAI</strong> (cualquier proveedor) y la
                    <strong> Messages API de Anthropic</strong> (solo provider keys de
                    Anthropic). Elige el que hable tu cliente — la misma clave virtual
                    sirve para los dos.
                  </>
                }
              />
              <div style={{ marginBottom: 16, fontSize: 12, color: '#8c8c8c' }}>
                El proxy vive en el executor backend, no en el frontend ni en el builder backend.
              </div>

              <Typography.Title level={5}>OpenAI-compatible · todos los proveedores</Typography.Title>
              <Text code>{`POST ${EXECUTOR_API}/proxy/v1/chat/completions`}</Text>
              <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
                Soporta <code>stream: true</code>. Para poder imputar el gasto, las
                respuestas en streaming incluyen siempre un chunk final de usage con{' '}
                <code>choices: []</code> — comportamiento estándar de{' '}
                <code>stream_options.include_usage</code>. Comprueba{' '}
                <code>chunk.choices</code> antes de indexarlo.
              </div>
              <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 6, fontSize: 12, overflowX: 'auto', marginTop: 8 }}>
{`curl ${EXECUTOR_API}/proxy/v1/chat/completions \\
  -H "Authorization: Bearer sk-cubicup-..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "gemini-2.5-flash",
    "messages": [{"role": "user", "content": "Hola!"}]
  }'`}
              </pre>
              <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 6, fontSize: 12, overflowX: 'auto' }}>
{`from openai import OpenAI

# El SDK añade /chat/completions al base_url, así que incluye /v1 aquí.
client = OpenAI(
    api_key="sk-cubicup-...",
    base_url="${EXECUTOR_API}/proxy/v1",
)

resp = client.chat.completions.create(
    model="gemini-2.5-flash",
    messages=[{"role": "user", "content": "Hola!"}],
)
print(resp.choices[0].message.content)`}
              </pre>

              <Typography.Title level={5} style={{ marginTop: 24 }}>
                Messages API de Anthropic · solo provider keys de Anthropic
              </Typography.Title>
              <Text code>{`POST ${EXECUTOR_API}/proxy/v1/messages`}</Text>
              <div style={{ marginTop: 8, marginBottom: 8, fontSize: 12, color: '#8c8c8c' }}>
                Soporta <code>stream: true</code>. Acepta la clave virtual en{' '}
                <code>x-api-key</code> o en <code>Authorization: Bearer</code>. Si la
                clave virtual apunta a un proveedor que no es Anthropic, este endpoint
                responde 400 — usa el de OpenAI.
              </div>

              <Typography.Text strong style={{ fontSize: 13 }}>Claude Code</Typography.Text>
              <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 6, fontSize: 12, overflowX: 'auto' }}>
{`export ANTHROPIC_BASE_URL="${EXECUTOR_API}/proxy"
export ANTHROPIC_AUTH_TOKEN="sk-cubicup-..."
# Define solo una de las dos: ANTHROPIC_API_KEY también funciona,
# pero tener ambas hace que el cliente mande dos cabeceras de auth.

claude`}
              </pre>

              <Typography.Text strong style={{ fontSize: 13 }}>Python (anthropic SDK)</Typography.Text>
              <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 6, fontSize: 12, overflowX: 'auto' }}>
{`from anthropic import Anthropic

# El SDK añade /v1/messages al base_url.
client = Anthropic(
    api_key="sk-cubicup-...",
    base_url="${EXECUTOR_API}/proxy",
)

with client.messages.stream(
    model="claude-opus-5",
    max_tokens=1024,
    messages=[{"role": "user", "content": "Hola!"}],
) as stream:
    for text in stream.text_stream:
        print(text, end="", flush=True)`}
              </pre>
            </div>
          ),
        },
      ]}
    />
  </AppPageLayout>
  );
};

export default ModelTokensPage;
